import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { errorMessage } from '../services/apiError';
import type { InviteSource } from '../services/gmailInviteService';
import { promotionService } from '../services/promotionService';
import { referralService, ReferralInfo } from '../services/referralService';

import { GmailInviteAll } from './GmailInviteAll';
import { ReferralLinkRow, ReferralProgram, ShareReelRow } from './ReferralProgram';
import { ShareEarnSection } from './ShareEarnSection';

import './ReferralModal.css';
import './ReferralModalTheme.css';

/** ``refer``: every way to earn, as rows. ``share``: the reel screen, opened from the Share a reel row. */
export type CreditMode = 'refer' | 'share';

interface ReferralModalProps {
  initialMode?: CreditMode;
  isOpen: boolean;
  onClose: () => void;
  projectId?: string;
  /** Why connecting Gmail for Invite all failed, when Google sent the user back with an error. */
  gmailNotice?: string | null;
  /** Google's consent page was declined or a box left unticked: show which boxes to tick, with Try again. */
  gmailPermission?: boolean;
  /** Where the window was opened from (the gift icon, the More menu...), kept on the invite batches it starts. */
  source?: InviteSource;
}

const MODE_COPY: Record<CreditMode, { description: string; title: string }> = {
  refer: {
    title: 'Refer & earn',
    description: 'Earn free credits. Invite friends or share a reel.',
  },
  share: {
    title: 'Reels & views',
    description: 'Post a reel of your project. Earn credits from its views.',
  },
};

const legacyCopyReferralLink = (link: string): void => {
  const textArea = document.createElement('textarea');

  textArea.value = link;
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);
};

const copyReferralLink = async (link: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(link);
  } catch {
    legacyCopyReferralLink(link);
  }
};

interface UseReferralInfoResult {
  error: string | null;
  info: ReferralInfo | null;
}

const useReferralInfo = (active: boolean): UseReferralInfoResult => {
  const [info, setInfo] = useState<ReferralInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchInfo = useCallback(async () => {
    setError(null);
    try {
      const data = await referralService.getReferralInfo();

      setInfo(data);
    } catch (err) {
      console.error('Failed to fetch referral info');
      setError(errorMessage(err, 'We couldn\u2019t load your referral link. Close this window and try again.'));
    }
  }, []);

  useEffect(() => {
    if (!active) return;
    void fetchInfo();
  }, [active, fetchInfo]);

  return { error, info };
};

const useReferralCopy = (link?: string) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!link) return;
    void copyReferralLink(link).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  };

  return { copied, handleCopy };
};

interface ModalBodyProps {
  mode: CreditMode;
  setMode: (mode: CreditMode) => void;
  projectId?: string;
  gmailNotice?: string | null;
  gmailPermission?: boolean;
  source?: InviteSource;
}

/** Every way to earn as rows, or the reel screen (opened from its row) with the way back. */
const ModalBody: React.FC<ModalBodyProps> = ({ mode, setMode, projectId, gmailNotice, gmailPermission, source }) => {
  const { error, info } = useReferralInfo(mode === 'refer');
  const { copied, handleCopy } = useReferralCopy(info?.referral_link);

  return mode === 'refer' ? (
    <ReferralProgram error={error} info={info}>
      <GmailInviteAll notice={gmailNotice} permission={gmailPermission} source={source}>
        <ReferralLinkRow copied={copied} info={info} onCopy={handleCopy} />
        <ShareReelRow onOpen={() => setMode('share')} />
      </GmailInviteAll>
    </ReferralProgram>
  ) : (
    <>
      <button className="referral-modal-back" onClick={() => setMode('refer')} type="button">
        <span aria-hidden="true">←</span> All ways to earn
      </button>
      <ShareEarnSection projectId={projectId} />
    </>
  );
};

interface ReferralModalContentProps {
  initialMode: CreditMode;
  onClose: () => void;
  projectId?: string;
  gmailNotice?: string | null;
  gmailPermission?: boolean;
  source?: InviteSource;
}

const ReferralModalContent: React.FC<ReferralModalContentProps> = ({
  initialMode, onClose, projectId, gmailNotice, gmailPermission, source,
}) => {
  const [mode, setMode] = useState<CreditMode>(initialMode);
  const copy = MODE_COPY[mode];

  useEffect(() => {
    promotionService.prefetchShareInfo();
  }, []);

  return (
    <div className="referral-modal-overlay" onClick={onClose}>
      <div
        aria-labelledby="free-credits-title"
        aria-modal="true"
        className={`referral-modal referral-modal--${mode}`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
      >
        <div className="referral-modal-header">
          <div className="referral-modal-heading">
            <span className="referral-modal-eyebrow">Earn credits</span>
            <h2 id="free-credits-title">{copy.title}</h2>
            <p>{copy.description}</p>
          </div>
          <button aria-label="Close" className="referral-modal-close" onClick={onClose} type="button">×</button>
        </div>

        <div className="referral-modal-body">
          <ModalBody gmailNotice={gmailNotice} gmailPermission={gmailPermission} mode={mode} projectId={projectId}
            setMode={setMode} source={source} />
        </div>
      </div>
    </div>
  );
};

// Portaled to <body> like every other modal here (see BillingModal). The
// sidebar this is rendered from is `position: absolute; z-index: 3` in the
// merged-timeline layout, which makes it a stacking context — left in place,
// the overlay's z-index: 10000 is clamped to that level and body-portaled
// chrome such as the artifact floating toolbar (z-index: 500) paints on top.
export const ReferralModal: React.FC<ReferralModalProps> = ({
  initialMode, isOpen, onClose, projectId, gmailNotice, gmailPermission, source,
}) => {
  if (!isOpen) return null;

  return createPortal(
    <ReferralModalContent
      gmailNotice={gmailNotice}
      gmailPermission={gmailPermission}
      initialMode={initialMode ?? (projectId ? 'share' : 'refer')}
      onClose={onClose}
      projectId={projectId}
      source={source}
    />,
    document.body,
  );
};
